import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import hrService from '../../../services/hrService';
import { useAuth } from '../../../contexts/AuthContext';

const LeaveBalance = () => {
  const { user } = useAuth();
  const [balanceData, setBalanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentBalance, setCurrentBalance] = useState(0);

  useEffect(() => {
    loadLeaveBalances();
  }, [user]);

  const loadLeaveBalances = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError('');
      
      // FIXED: Handle the response structure from hrService.getLeaveBalances()
      const balancesResponse = await hrService?.getLeaveBalances?.();
      
      // Check if it's an error response structure with NO_EMPLOYEE_RECORD
      if (balancesResponse && balancesResponse?.error === 'NO_EMPLOYEE_RECORD') {
        // Handle the case where user doesn't have an employee record
        setBalanceData([]);
        setCurrentBalance(0);
        setError(balancesResponse?.message || 'Employee record not found. Please contact HR.');
        return;
      }
      
      // If it's a normal array response, use it directly
      const balances = Array.isArray(balancesResponse) ? balancesResponse : (balancesResponse?.balances || []);
      setBalanceData(balances);
      
      // FIXED: Only call getCurrentLeaveBalanceSummary if we have valid balance data
      if (balances && balances?.length > 0) {
        try {
          const summary = await hrService?.getCurrentLeaveBalanceSummary?.();
          setCurrentBalance(summary?.currentBalance || 0);
        } catch (summaryError) {
          console.warn('Error getting current balance summary:', summaryError);
          // Use the latest balance from the array as fallback
          const latestBalance = balances?.[0];
          setCurrentBalance(parseFloat(latestBalance?.closingBalance) || 0);
        }
      } else {
        setCurrentBalance(0);
      }
      
    } catch (err) {
      console.error('Error loading leave balances:', err);
      setError(`Failed to load leave balances: ${err?.message}`);
      setBalanceData([]);
      setCurrentBalance(0);
    } finally {
      setLoading(false);
    }
  };

  const getMonthName = (monthNumber) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months?.[monthNumber - 1];
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            <span className="text-foreground">Loading leave balance...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="text-center py-8">
          <Icon name="AlertCircle" size={48} color="var(--color-error)" className="mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Employee Setup Required</h3>
          <p className="text-muted-foreground mb-4">{error}</p>
          <button
            onClick={loadLeaveBalances}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Balance Summary */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground mb-2">Current Leave Balance</h2>
          <div className="text-4xl font-bold text-primary mb-4">{currentBalance} days</div>
          <p className="text-muted-foreground">
            Available leave days you can use for time off
          </p>
        </div>
      </div>
      
      {/* Leave Accrual Information */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <Icon name="Info" size={24} color="var(--color-primary)" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground mb-3">How Leave Balance Works</h3>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                <strong className="text-foreground">Monthly Accrual:</strong> You earn 1.5 leave days per month when you maintain full attendance without taking any leave.
              </p>
              <p>
                <strong className="text-foreground">Carry Forward:</strong> Unused leave carries forward to the next month and remains eligible year over year.
              </p>
              <p>
                <strong className="text-foreground">Eligibility:</strong> Leave is accrued based on your attendance record for each month.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Balance History */}
      <div className="bg-card rounded-lg border border-border">
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Balance History</h3>
          <p className="text-muted-foreground mt-1">Monthly leave balance breakdown</p>
        </div>

        <div className="p-6">
          {!Array.isArray(balanceData) || balanceData?.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="BarChart3" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <h4 className="text-lg font-medium text-foreground mb-2">No Balance History</h4>
              <p className="text-muted-foreground">
                Your leave balance history will appear here as you accrue leave days each month.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left border-b border-border">
                    <th className="pb-3 text-sm font-medium text-muted-foreground">Period</th>
                    <th className="pb-3 text-sm font-medium text-muted-foreground">Opening</th>
                    <th className="pb-3 text-sm font-medium text-muted-foreground">Earned</th>
                    <th className="pb-3 text-sm font-medium text-muted-foreground">Used</th>
                    <th className="pb-3 text-sm font-medium text-muted-foreground">Closing</th>
                    <th className="pb-3 text-sm font-medium text-muted-foreground">Eligible</th>
                  </tr>
                </thead>
                <tbody>
                  {balanceData?.map((balance, index) => (
                    <tr key={`${balance?.year}-${balance?.month}-${index}`} className="border-b border-border">
                      <td className="py-3 text-sm text-foreground font-medium">
                        {getMonthName(balance?.month)} {balance?.year}
                      </td>
                      <td className="py-3 text-sm text-foreground">
                        {balance?.openingBalance || 0}
                      </td>
                      <td className="py-3 text-sm text-success">
                        +{balance?.earnedBalance || 0}
                      </td>
                      <td className="py-3 text-sm text-error">
                        -{balance?.usedBalance || 0}
                      </td>
                      <td className="py-3 text-sm text-foreground font-semibold">
                        {balance?.closingBalance || 0}
                      </td>
                      <td className="py-3 text-sm">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          balance?.isEligibleForAccrual 
                            ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'
                        }`}>
                          <Icon 
                            name={balance?.isEligibleForAccrual ? "CheckCircle" : "AlertCircle"} 
                            size={12} 
                            className="mr-1"
                          />
                          {balance?.isEligibleForAccrual ? 'Yes' : 'No'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaveBalance;